// recova GmbH – System: Statistiken, Benutzer, Backups, Preis-Einstellungen, Demodaten, Self-Hosting/ZIP-Export.
import { useEffect, useState, useCallback } from "react";
import { API, fmtErr, eur, BACKEND } from "@/lib/api";
import { useAuth } from "@/App";
import { DashboardLayout, BACKOFFICE_SECTIONS } from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BarChart3, Users, HardDriveDownload, Server, Plus, Download, DatabaseBackup, Settings, Trash2 } from "lucide-react";
import { toast } from "sonner";

const ROLE_LABELS = { backoffice: "Backoffice", technician: "Techniker" };

export default function AdminDashboard() {
  const { user } = useAuth();
  const [tab, setTab] = useState("stats");
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [backups, setBackups] = useState([]);
  const [showUserDialog, setShowUserDialog] = useState(false);
  const [newUser, setNewUser] = useState({ email: "", password: "", name: "", role: "technician", phone: "", van_id: "", van_plate: "" });
  const [settings, setSettings] = useState({ labor_rate_hour: 68, diagnosis_fee: 79, imap_host: "", imap_port: 993, imap_user: "", imap_password: "", imap_folder: "INBOX", imap_ssl: true, imap_enabled: false });
  const [confirmDemo, setConfirmDemo] = useState(false);
  const [busy, setBusy] = useState(false);

  const loadStats = useCallback(() => API.get("/admin/stats").then((r) => setStats(r.data)).catch((e) => toast.error(fmtErr(e))), []);
  const loadUsers = useCallback(() => API.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {}), []);
  const loadBackups = useCallback(() => API.get("/admin/backups").then((r) => setBackups(r.data)).catch(() => {}), []);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => {
    if (tab === "users") loadUsers();
    if (tab === "backups") loadBackups();
    if (tab === "einstellungen") API.get("/admin/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, [tab, loadUsers, loadBackups]);

  const saveSettings = async () => {
    setBusy(true);
    try {
      const r = await API.patch("/admin/settings", {
        labor_rate_hour: Number(settings.labor_rate_hour), diagnosis_fee: Number(settings.diagnosis_fee),
        imap_host: settings.imap_host || null, imap_port: Number(settings.imap_port) || 993,
        imap_user: settings.imap_user || null, imap_password: settings.imap_password || null,
        imap_folder: settings.imap_folder || "INBOX", imap_ssl: !!settings.imap_ssl, imap_enabled: !!settings.imap_enabled,
      });
      setSettings(r.data);
      toast.success("Einstellungen gespeichert");
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const clearDemoData = async () => {
    setBusy(true);
    try {
      const r = await API.post("/admin/demo-data/clear");
      const total = Object.values(r.data.deleted || {}).reduce((a, b) => a + b, 0);
      toast.success(`Demodaten vollständig gelöscht (${total} Datensätze)`);
      setConfirmDemo(false);
      loadStats();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const downloadSource = async () => {
    try {
      const r = await API.get("/admin/export/source", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "recova-system.zip";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Systemabbild wird heruntergeladen");
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const createUser = async () => {
    if (!newUser.email || !newUser.password || !newUser.name) return toast.error("Name, E-Mail und Passwort erforderlich");
    setBusy(true);
    try {
      await API.post("/admin/users", { ...newUser, van_id: newUser.van_id || null, van_plate: newUser.van_plate || null, phone: newUser.phone || null });
      toast.success("Benutzer angelegt");
      setShowUserDialog(false);
      setNewUser({ email: "", password: "", name: "", role: "technician", phone: "", van_id: "", van_plate: "" });
      loadUsers();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (u) => {
    await API.patch(`/admin/users/${u.id}`, { active: !u.active });
    loadUsers();
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Benutzer ${u.name} (${u.email}) wirklich endgültig löschen?`)) return;
    try {
      await API.delete(`/admin/users/${u.id}`);
      toast.success("Benutzer gelöscht");
      setUsers(users.filter((x) => x.id !== u.id));
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const runBackup = async () => {
    setBusy(true);
    try {
      const r = await API.post("/admin/backups/run");
      toast.success(`Backup erstellt: ${r.data.filename}`);
      loadBackups();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const downloadBackup = async (b) => {
    try {
      const r = await API.get(`/admin/backups/${b.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = b.filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  return (
    <DashboardLayout title="System-Administration" subtitle="Backoffice – System"
      sections={BACKOFFICE_SECTIONS.map((s) => ({ ...s, active: s.to === "/app/admin" }))}
      active={tab} onNav={setTab}
      nav={[
        { key: "stats", label: "Übersicht", icon: <BarChart3 className="h-4 w-4" /> },
        { key: "users", label: "Benutzer & Rollen", icon: <Users className="h-4 w-4" /> },
        { key: "backups", label: "Backups", icon: <HardDriveDownload className="h-4 w-4" /> },
        { key: "einstellungen", label: "Einstellungen", icon: <Settings className="h-4 w-4" /> },
        { key: "selfhost", label: "Self-Hosting", icon: <Server className="h-4 w-4" /> },
      ]}>

      {tab === "stats" && stats && (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="admin-stats">
          {[
            { label: "Aufträge gesamt", value: stats.orders_total },
            { label: "Umsatz (bezahlt)", value: eur(stats.revenue_paid) },
            { label: "Offene E-Mail-Intakes", value: stats.open_intake },
            { label: "Offene Lieferanten-Bestellungen", value: stats.open_part_orders },
            { label: "Offene Reservierungen (Pickliste)", value: stats.open_reservations },
            { label: "Teile unter Mindestbestand", value: stats.low_stock_parts },
            { label: "Benutzer", value: stats.users },
            { label: "Ausgestellte Gutscheine", value: stats.vouchers },
          ].map((s, i) => (
            <div key={i} className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`stat-card-${i}`}>
              <p className="text-xs uppercase tracking-widest text-slate-400">{s.label}</p>
              <p className="font-heading mt-1 text-2xl font-extrabold text-[#1b4332]">{s.value}</p>
            </div>
          ))}
          <div className="col-span-2 rounded-2xl border border-slate-200 bg-white p-5 lg:col-span-4" data-testid="stat-status-breakdown">
            <p className="mb-3 text-xs uppercase tracking-widest text-slate-400">Aufträge nach Status</p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(stats.by_status).map(([k, v]) => (
                <span key={k} className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">{k}: {v}</span>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === "users" && (
        <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="users-panel">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-heading text-lg font-semibold text-[#1b4332]">Benutzer &amp; Rollen</h3>
            <Button className="bg-[#1b4332] hover:bg-[#143326]" onClick={() => setShowUserDialog(true)} data-testid="new-user-button"><Plus className="mr-1.5 h-4 w-4" /> Neuer Benutzer</Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="users-table">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-400">
                  <th className="pb-2 pr-4">Name</th><th className="pb-2 pr-4">E-Mail</th><th className="pb-2 pr-4">Rolle</th><th className="pb-2 pr-4">Fahrzeug</th><th className="pb-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-slate-100" data-testid={`user-row-${u.email}`}>
                    <td className="py-2.5 pr-4 font-semibold">{u.name}</td>
                    <td className="py-2.5 pr-4">{u.email}</td>
                    <td className="py-2.5 pr-4"><span className="badge-tech">{ROLE_LABELS[u.role] || u.role}</span></td>
                    <td className="py-2.5 pr-4">{u.van_id ? `${u.van_id} (${u.van_plate || "-"})` : "–"}</td>
                    <td className="py-2.5">
                      {u.id !== user.id && (
                        <div className="flex items-center gap-2">
                          <Button size="sm" variant="outline" onClick={() => toggleActive(u)} data-testid={`toggle-user-${u.email}`}>
                            {u.active ? "Deaktivieren" : "Aktivieren"}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => deleteUser(u)} data-testid={`delete-user-${u.email}`}>
                            <Trash2 className="h-3.5 w-3.5 text-red-400" />
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "backups" && (
        <div className="space-y-4" data-testid="backups-panel">
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-5">
            <div className="text-sm text-slate-600">
              <p className="font-semibold text-[#0f172a]">Automatische Backups: täglich 00:00 Uhr (Cron)</p>
              <p>Verschlüsselte Archive (.tar.gz.enc) mit komplettem Datenbank-Dump + allen Medien-Uploads.</p>
            </div>
            <Button className="bg-[#1b4332] hover:bg-[#143326]" onClick={runBackup} disabled={busy} data-testid="run-backup-button">
              <DatabaseBackup className="mr-2 h-4 w-4" /> {busy ? "Erstelle Backup…" : "Backup jetzt erstellen"}
            </Button>
          </div>
          {backups.map((b) => (
            <div key={b.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-4" data-testid={`backup-row-${b.id}`}>
              <div>
                <p className="font-mono2 text-sm font-semibold">{b.filename}</p>
                <p className="text-xs text-slate-500">{new Date(b.created_at).toLocaleString("de-DE")} · {(b.size_bytes / 1024).toFixed(0)} KB · Auslöser: {b.trigger} · {(b.includes || []).join(" + ")}</p>
              </div>
              <Button size="sm" variant="outline" onClick={() => downloadBackup(b)} data-testid={`download-backup-${b.id}`}>
                <Download className="mr-1.5 h-4 w-4" /> Download Full System Backup
              </Button>
            </div>
          ))}
          {backups.length === 0 && <p className="rounded-xl border border-dashed border-slate-300 bg-white py-10 text-center text-slate-400" data-testid="backups-empty">Noch keine Backups vorhanden.</p>}
        </div>
      )}

      {tab === "einstellungen" && (
        <div className="max-w-lg rounded-2xl border border-slate-200 bg-white p-6" data-testid="settings-panel">
          <h3 className="font-heading text-lg font-semibold text-[#1b4332]">Preis-Konfiguration</h3>
          <p className="mt-1 text-sm text-slate-500">Diese Werte werden in Angeboten, Check-Out und Rechnungen verwendet.</p>
          <div className="mt-4 space-y-4">
            <div>
              <Label>Stundensatz Techniker (EUR/h)</Label>
              <Input type="number" min="1" step="0.5" className="mt-1" value={settings.labor_rate_hour}
                onChange={(e) => setSettings({ ...settings, labor_rate_hour: e.target.value })} data-testid="settings-labor-rate" />
            </div>
            <div>
              <Label>Anfahrts- & Diagnosepauschale (EUR)</Label>
              <Input type="number" min="1" step="0.5" className="mt-1" value={settings.diagnosis_fee}
                onChange={(e) => setSettings({ ...settings, diagnosis_fee: e.target.value })} data-testid="settings-diagnosis-fee" />
            </div>
            <Button className="bg-[#1b4332] hover:bg-[#143326]" onClick={saveSettings} disabled={busy} data-testid="settings-save">
              {busy ? "Speichern…" : "Einstellungen speichern"}
            </Button>
          </div>
          <div className="mt-6 border-t border-slate-200 pt-5">
            <h3 className="font-heading text-lg font-semibold text-[#1b4332]">E-Mail-Eingang (IMAP)</h3>
            <p className="mt-1 text-sm text-slate-500">Postfach für eingehende Kundenanfragen. Der Abruf erfolgt manuell in der E-Mail-Queue (Aufträge & Disposition).</p>
            <div className="mt-4 space-y-4">
              <label className="flex items-center gap-2 text-sm font-semibold text-slate-700" data-testid="imap-enabled-label">
                <input type="checkbox" className="h-4 w-4 accent-[#1b4332]" checked={!!settings.imap_enabled}
                  onChange={(e) => setSettings({ ...settings, imap_enabled: e.target.checked })} data-testid="settings-imap-enabled" />
                IMAP-Abruf aktiviert
              </label>
              <div>
                <Label>IMAP-Server (Host)</Label>
                <Input className="mt-1" placeholder="z. B. imap.strato.de" value={settings.imap_host || ""}
                  onChange={(e) => setSettings({ ...settings, imap_host: e.target.value })} data-testid="settings-imap-host" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Port</Label>
                  <Input type="number" className="mt-1" value={settings.imap_port ?? 993}
                    onChange={(e) => setSettings({ ...settings, imap_port: e.target.value })} data-testid="settings-imap-port" />
                </div>
                <div>
                  <Label>Ordner</Label>
                  <Input className="mt-1" placeholder="INBOX" value={settings.imap_folder || ""}
                    onChange={(e) => setSettings({ ...settings, imap_folder: e.target.value })} data-testid="settings-imap-folder" />
                </div>
              </div>
              <div>
                <Label>Benutzername / E-Mail</Label>
                <Input className="mt-1" placeholder="reparatur@recova.de" value={settings.imap_user || ""}
                  onChange={(e) => setSettings({ ...settings, imap_user: e.target.value })} data-testid="settings-imap-user" />
              </div>
              <div>
                <Label>Passwort</Label>
                <Input type="password" className="mt-1" placeholder="(unverändert lassen)" value={settings.imap_password || ""}
                  onChange={(e) => setSettings({ ...settings, imap_password: e.target.value })} data-testid="settings-imap-password" />
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-700" data-testid="imap-ssl-label">
                <input type="checkbox" className="h-4 w-4 accent-[#1b4332]" checked={!!settings.imap_ssl}
                  onChange={(e) => setSettings({ ...settings, imap_ssl: e.target.checked })} data-testid="settings-imap-ssl" />
                SSL/TLS verwenden (empfohlen, Port 993)
              </label>
            </div>
          </div>
          <div className="mt-6 rounded-xl border-2 border-red-200 bg-red-50 p-4" data-testid="danger-zone">
            <p className="text-sm font-bold text-red-700">Gefahrenzone</p>
            {stats?.demo_data_cleared ? (
              <p className="mt-1 text-xs text-red-600" data-testid="demo-clear-done">
                Demodaten wurden bereits vollständig gelöscht (Aufträge, Teilestamm, Lagerplätze, Bestände, Reservierungen, Bestellungen, Gutscheine, Intake). Das System läuft im produktiven Zustand; diese Aktion ist daher nicht mehr verfügbar.
              </p>
            ) : (
              <>
                <p className="mt-1 text-xs text-red-600">Löscht unwiderruflich alle Aufträge, den kompletten Teilestamm, Lagerplätze, Bestände, Reservierungen, Bestellungen, Gutscheine, Intake-Daten und Login-Sperren. Benutzer sowie Preis-/IMAP-Einstellungen bleiben erhalten. Wird auch nach einem Neustart/Rebuild nicht erneut mit Demodaten befüllt.</p>
                {!confirmDemo ? (
                  <Button variant="outline" className="mt-3 border-red-300 text-red-600 hover:bg-red-100" onClick={() => setConfirmDemo(true)} data-testid="demo-clear-open">
                    Demodaten löschen
                  </Button>
                ) : (
                  <div className="mt-3 flex gap-2">
                    <Button className="bg-red-600 hover:bg-red-700" onClick={clearDemoData} disabled={busy} data-testid="demo-clear-confirm">
                      {busy ? "Lösche…" : "Ja, endgültig löschen"}
                    </Button>
                    <Button variant="outline" onClick={() => setConfirmDemo(false)} data-testid="demo-clear-cancel">Abbrechen</Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {tab === "selfhost" && (
        <div className="max-w-2xl space-y-4" data-testid="selfhost-panel">
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <h3 className="font-heading text-lg font-semibold text-[#1b4332]">Anti-Lock-In: Self-Hosting-Paket</h3>
            <p className="mt-2 text-sm text-slate-600">Der komplette Quellcode inkl. Docker-Artefakten liegt im Repository unter <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">/app/selfhost/</code>:</p>
            <ul className="mt-3 space-y-1.5 text-sm text-slate-600">
              <li>• <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">docker-compose.yml</code> – App + MongoDB + Caddy (Let's Encrypt SSL)</li>
              <li>• <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">deploy_vserver.sh</code> – One-Click-Installer: <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">chmod +x deploy_vserver.sh && ./deploy_vserver.sh</code></li>
              <li>• <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">restore_backup.sh</code> – stellt DB &amp; Medien auf frischem Server wieder her</li>
              <li>• <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">Dockerfile.selfhost</code> – All-in-One-Build (Frontend + Backend)</li>
            </ul>
            <Button className="mt-4 bg-[#1b4332] hover:bg-[#143326]" onClick={downloadSource} data-testid="download-source-zip">
              <Download className="mr-2 h-4 w-4" /> Systemabbild als ZIP herunterladen
            </Button>
            <p className="mt-2 text-xs text-slate-400">Enthält Quellcode, Docker-Dateien, Installer, Cron-Konfiguration und eine .env.example (ohne Secrets, ohne Nutzdaten).</p>
          </div>
          <div className="rounded-2xl border border-[#028090]/25 bg-cyan-50 p-6 text-sm text-[#026673]">
            <p className="font-semibold">Hinweis zur Datenbank</p>
            <p className="mt-1">Die Plattform nutzt MongoDB. Backups werden via mongodump erstellt und mit dem BACKUP_SECRET verschlüsselt – bitte sicher verwahren.</p>
          </div>
        </div>
      )}

      {showUserDialog && (
        <Dialog open onOpenChange={setShowUserDialog}>
          <DialogContent className="max-w-md" data-testid="new-user-dialog">
            <DialogHeader><DialogTitle>Neuen Benutzer anlegen</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Input placeholder="Name *" value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} data-testid="new-user-name" />
              <Input placeholder="E-Mail *" type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} data-testid="new-user-email" />
              <Input placeholder="Passwort *" type="password" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} data-testid="new-user-password" />
              <div>
                <Label>Rolle</Label>
                <select className="mt-1 w-full rounded-md border border-slate-300 bg-white px-2 py-2 text-sm" value={newUser.role} onChange={(e) => setNewUser({ ...newUser, role: e.target.value })} data-testid="new-user-role">
                  {Object.entries(ROLE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              {newUser.role === "technician" && (
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder="Van-ID (z. B. VAN-02)" value={newUser.van_id} onChange={(e) => setNewUser({ ...newUser, van_id: e.target.value })} data-testid="new-user-van" />
                  <Input placeholder="Kennzeichen" value={newUser.van_plate} onChange={(e) => setNewUser({ ...newUser, van_plate: e.target.value })} data-testid="new-user-plate" />
                </div>
              )}
              <Button className="w-full bg-[#1b4332] hover:bg-[#143326]" onClick={createUser} disabled={busy} data-testid="new-user-submit">
                {busy ? "Anlegen…" : "Benutzer anlegen"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </DashboardLayout>
  );
}
